import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { authApi } from '../../services/api';
import styles from './Auth.module.css';

export const RegisterForm: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [canRegister, setCanRegister] = useState(false);
  const [checking, setChecking] = useState(true);
  
  const register = useAuthStore((state) => state.register);
  const navigate = useNavigate();

  useEffect(() => {
    checkCanRegister();
  }, []);

  const checkCanRegister = async () => {
    try {
      const response = await authApi.canRegister();
      setCanRegister(response.data.canRegister);
    } catch (err) {
      setError('Failed to check registration status');
    } finally {
      setChecking(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await register(username, password, email || undefined);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to register');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className={styles.authContainer}>
        <div className={styles.authBox}>
          <p className={styles.authSubtitle}>Checking registration status...</p>
        </div>
      </div>
    );
  }

  if (!canRegister) {
    return (
      <div className={styles.authContainer}>
        <div className={styles.authBox}>
          <h1 className={styles.authTitle}>Registration Closed</h1>
          <p className={styles.authSubtitle}>
            Registration is currently closed. Please contact an administrator for an account.
          </p>
          <div className={styles.formLink}>
            Already have an account? <Link to="/login">Login</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.authContainer}>
      <div className={styles.authBox}>
        <h1 className={styles.authTitle}>Create Account</h1>
        <p className={styles.authSubtitle}>You'll be the first admin user</p>
        
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Username</label>
            <input
              type="text"
              className={styles.formInput}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={3}
              autoFocus
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Email (optional)</label>
            <input
              type="email"
              className={styles.formInput}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Password</label>
            <input
              type="password"
              className={styles.formInput}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>

          {error && <div className={styles.formError}>{error}</div>}

          <button
            type="submit"
            className={styles.formButton}
            disabled={loading}
          >
            {loading ? 'Creating account...' : 'Register'}
          </button>
        </form>

        <div className={styles.formLink}>
          Already have an account? <Link to="/login">Login</Link>
        </div>
      </div>
    </div>
  );
};
